'use client';

/**
 * @brisa/ui · facelift/login-brand-panel/LoginBrandPanel
 *
 * Feature: admin-basico-login (W3.2) · UI_SPEC §2.3 (Painel lateral — media panel)
 *
 * Painel decorativo do login redesenhado (facelift v1). Carrega uma mídia
 * (imagem ou vídeo) aleatória de um manifesto a cada visita — simulando o
 * endpoint dinâmico que virá de um CRUD em /admin. Vídeo roda em loop
 * (muted/playsInline) ocupando todo o painel via object-cover.
 *
 * No MVP, conteúdo padrão = 7 vídeos do Hero da Home (/landing/hero/videoXX.mp4),
 * declarados em /login-media.json (raiz pública — NÃO /admin/*, porque o
 * middleware Regra 8 captura /admin/* e redirecionaria o fetch para login,
 * quebrando o painel decorativo). Quando o CRUD ficar pronto, esta lista
 * vira fetch dinâmico do backend mantendo o mesmo shape MediaItem.
 *
 * Props:
 *   - mediaItems   override controlado da lista (Storybook/tests).
 *                  Quando passado, ignora manifestUrl.
 *   - manifestUrl  URL do JSON manifesto. Default: /login-media.json.
 *   - footerLabel  texto pinned bottom; passe null pra ocultar. Default:
 *                  "BRISA PASS · BARCELONA 2026". Texto vai em cima da mídia —
 *                  drop-shadow + tom claro garantem legibilidade.
 *
 * Acessibilidade: aria-hidden=true no root (decorativo, sem conteúdo
 * semântico). O brand mark semântico do login fica no painel esquerdo.
 */
import * as React from 'react';

import { cn } from '@brisa/ui/lib/utils';

export type LoginBrandMediaItem =
  | { type: 'video'; src: string }
  | { type: 'image'; src: string; alt?: string };

export interface LoginBrandPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Override controlado (Storybook/tests). Quando passado, ignora `manifestUrl`. */
  mediaItems?: LoginBrandMediaItem[];
  /** URL do JSON manifesto. Default: /admin/login-media.json. */
  manifestUrl?: string;
  /**
   * Texto do footer discreto pinned bottom-left. Default:
   * "BRISA PASS · BARCELONA 2026". Passe `null` para ocultar.
   */
  footerLabel?: string | null;
}

const DEFAULT_FOOTER = 'BRISA PASS · BARCELONA 2026';
const DEFAULT_MANIFEST_URL = '/login-media.json';

function pickRandom<T>(items: T[]): T | null {
  if (items.length === 0) return null;
  const i = Math.floor(Math.random() * items.length);
  return items[i] ?? null;
}

interface ManifestShape {
  items?: LoginBrandMediaItem[];
}

export function LoginBrandPanel({
  mediaItems,
  manifestUrl = DEFAULT_MANIFEST_URL,
  footerLabel = DEFAULT_FOOTER,
  className,
  ...props
}: LoginBrandPanelProps) {
  const [selected, setSelected] = React.useState<LoginBrandMediaItem | null>(null);

  React.useEffect(() => {
    let cancelled = false;

    // Override controlado tem prioridade sobre fetch — necessário pra
    // Storybook/tests determinísticos.
    if (mediaItems && mediaItems.length > 0) {
      setSelected(pickRandom(mediaItems));
      return;
    }

    fetch(manifestUrl)
      .then((r) => (r.ok ? (r.json() as Promise<ManifestShape>) : Promise.reject(r)))
      .then((data) => {
        if (cancelled) return;
        const items = Array.isArray(data.items) ? data.items : [];
        setSelected(pickRandom(items));
      })
      .catch(() => {
        if (cancelled) return;
        setSelected(null);
      });

    return () => {
      cancelled = true;
    };
  }, [mediaItems, manifestUrl]);

  return (
    <div
      aria-hidden="true"
      data-slot="login-brand-panel"
      className={cn(
        'relative flex h-full min-h-full w-full items-center justify-center overflow-hidden',
        'bg-canvas-gradient',
        className,
      )}
      {...props}
    >
      {selected ? (
        <div
          data-slot="brand-panel-media"
          className={cn(
            'absolute inset-0 h-full w-full',
            'animate-in fade-in-0 duration-slow ease-in-out motion-reduce:animate-none',
          )}
        >
          {selected.type === 'video' ? (
            <video
              key={selected.src}
              src={selected.src}
              autoPlay
              muted
              loop
              playsInline
              className="h-full w-full object-cover"
            />
          ) : (
            <img
              src={selected.src}
              alt={selected.alt ?? ''}
              className="h-full w-full object-cover"
              loading="eager"
            />
          )}
        </div>
      ) : null}
      {footerLabel ? (
        <span
          data-slot="brand-panel-footer"
          className={cn(
            'absolute bottom-8 left-12 right-12 z-10 font-body text-[13px] font-semibold uppercase tracking-[0.08em]',
            // Texto claro com drop-shadow garante legibilidade sobre qualquer
            // frame de vídeo (luminância varia ao longo do loop).
            'text-white [text-shadow:0_1px_2px_rgba(0,0,0,0.45)]',
            'animate-in fade-in-0 duration-slow ease-out motion-reduce:animate-none',
          )}
        >
          {footerLabel}
        </span>
      ) : null}
    </div>
  );
}
